<div [class]="inputClasses()">
  <!-- Currency Input -->
  @if (type() === 'currency') {
    <div class="v-one-input__wrapper">
      <input
        #inputRef
        [id]="'input-currency-' + label()"
        type="text"
        inputmode="decimal"
        [value]="currencyText()"
        [placeholder]="placeholder()"
        [disabled]="isDisabled()"
        [readonly]="readonly()"
        [required]="required()"
        [attr.aria-label]="label()"
        [attr.aria-invalid]="hasError()"
        [attr.aria-required]="required()"
        class="v-one-input__field"
        (input)="onCurrencyInput($event)"
        (focus)="onFocus()"
        (blur)="onBlur()"
      />

      <label class="v-one-input__label" [for]="'input-currency-' + label()">
        {{ label() }}
        @if (required()) {
          <span class="v-one-input__required">*</span>
        }
      </label>
    </div>
  }

  <!-- Text Input -->
  @if (type() === 'text') {
    <div class="v-one-input__wrapper">
      <!-- Left Icon -->
      @if (leftIcon()) {
        <div class="v-one-input__icon v-one-input__icon--left">
          <v-one-icon
            [name]="leftIcon()"
            [color]="
              hasError() ? 'var(--color-danger-600)' : 'var(--color-gray-700)'
            "
          />
        </div>
      }

      <!-- Input Element -->
      <input
        #inputRef
        [id]="'input-text-' + label()"
        type="text"
        [value]="value()"
        [attr.maxlength]="maxlength()"
        [placeholder]="placeholder()"
        [disabled]="isDisabled()"
        [readonly]="readonly()"
        [required]="required()"
        [attr.aria-label]="label()"
        [attr.aria-invalid]="hasError()"
        [attr.aria-required]="required()"
        class="v-one-input__field"
        (input)="onInputChange($event)"
        (focus)="onFocus()"
        (blur)="onBlur()"
        (keydown)="onKeydown($event)"
      />

      <!-- Label -->
      <label class="v-one-input__label" [for]="'input-text-' + label()">
        {{ label() }}
        @if (required()) {
          <span class="v-one-input__required">*</span>
        }
      </label>

      <!-- Clear Button -->
      @if (showClearButton()) {
        <button
          type="button"
          class="v-one-input__clear"
          (mousedown)="$event.preventDefault()"
          (click)="onClear()"
          [attr.aria-label]="'Clear input'"
          tabindex="-1"
        >
          <v-one-icon
            [name]="'clean'"
            [color]="'var(--color-black)'"
            [type]="ICON_TYPE.FILLED"
          />
        </button>
      }
    </div>
  }

  <!-- Textarea Input -->
  @if (type() === 'textarea') {
    <div class="v-one-input__wrapper">
      @if (leftIcon()) {
        <div class="v-one-input__icon v-one-input__icon--left">
          <v-one-icon
            [name]="leftIcon()"
            [color]="
              hasError() ? 'var(--color-danger-600)' : 'var(--color-gray-700)'
            "
          />
        </div>
      }

      <!-- Textarea Element -->
      <textarea
        #inputRef
        [id]="'input-textarea-' + label()"
        [value]="value()"
        [rows]="rows()"
        [attr.maxlength]="maxlength()"
        [placeholder]="placeholder()"
        [disabled]="isDisabled()"
        [required]="required()"
        [attr.aria-label]="label()"
        [attr.aria-invalid]="hasError()"
        [attr.aria-required]="required()"
        class="v-one-input__field v-one-input__field--textarea"
        (input)="onInputChange($event)"
        (focus)="onFocus()"
        (blur)="onBlur()"
        (keydown)="onKeydown($event)"
        [innerText]="value()"
        role="textbox"
      ></textarea>

      <!-- Label -->
      <label class="v-one-input__label" [for]="'input-textarea-' + label()">
        {{ label() }}
        @if (required()) {
          <span class="v-one-input__required">*</span>
        }
      </label>

      <!-- Clear Button -->
      @if (showClearButton()) {
        <button
          type="button"
          class="v-one-input__clear"
          (mousedown)="$event.preventDefault()"
          (click)="onClear()"
          [attr.aria-label]="'Clear ' + label()"
        >
          <v-one-icon
            [name]="'clean'"
            [color]="'var(--color-black)'"
            [type]="ICON_TYPE.FILLED"
          />
        </button>
      }
    </div>
  }

  <!-- Number Input -->
  @if (type() === 'number') {
    <div class="v-one-input__wrapper">
      <!-- Input Element -->
      <input
        #inputRef
        [id]="'input-number-' + label()"
        type="number"
        [value]="value()"
        [placeholder]="placeholder()"
        [disabled]="isDisabled()"
        [readonly]="readonly()"
        [required]="required()"
        [min]="min()"
        [max]="max()"
        [step]="step()"
        [attr.aria-label]="label()"
        [attr.aria-invalid]="hasError()"
        [attr.aria-required]="required()"
        class="v-one-input__field v-one-input__field--number"
        (input)="onInputChange($event)"
        (focus)="onFocus()"
        (blur)="onBlur()"
        (keydown)="onKeydown($event)"
      />

      <!-- Label -->
      <label class="v-one-input__label" [for]="'input-number-' + label()">
        {{ label() }}
        @if (required()) {
          <span class="v-one-input__required">*</span>
        }
      </label>
    </div>
  }

  <!-- Date Input -->
  @if (type() === 'date') {
    <div class="v-one-input__wrapper">
      <!-- Input Element -->
      <input
        #inputRef
        [id]="'input-date-' + label()"
        type="date"
        [value]="value()"
        [placeholder]="placeholder()"
        [disabled]="isDisabled()"
        [readonly]="readonly()"
        [required]="required()"
        [attr.aria-label]="label()"
        [attr.aria-invalid]="hasError()"
        [attr.aria-required]="required()"
        class="v-one-input__field v-one-input__field--date"
        [class.v-one-input__field--ios]="isIOS()"
        (input)="onInputChange($event)"
        (focus)="onFocus()"
        (blur)="onBlur()"
        (keydown)="onKeydown($event)"
      />

      <!-- Label -->
      <label class="v-one-input__label" [for]="'input-date-' + label()">
        {{ label() }}
        @if (required()) {
          <span class="v-one-input__required">*</span>
        }
      </label>
    </div>
  }

  <!-- Select Input (Custom Dropdown) -->
  @if (type() === 'select') {
    <div class="v-one-input__wrapper">
      <!-- Custom Select Display -->
      <div
        #selectRef
        [id]="'input-select-' + label()"
        [attr.tabindex]="isDisabled() ? -1 : 0"
        [attr.aria-label]="label()"
        [attr.aria-invalid]="hasError()"
        [attr.aria-required]="required()"
        [attr.aria-expanded]="isDropdownOpen()"
        class="v-one-input__field v-one-input__field--select"
        (click)="onSelectClick()"
        (focus)="onFocus()"
        (blur)="onBlur()"
        (keydown)="onKeydown($event)"
      >
        {{ getSelectedLabel() || placeholder() }}
      </div>

      <!-- Label -->
      <label class="v-one-input__label" [for]="'input-select-' + label()">
        {{ label() }}
        @if (required()) {
          <span class="v-one-input__required">*</span>
        }
      </label>

      <!-- Chevron Icon -->
      <div class="v-one-input__icon v-one-input__icon--right">
        <div
          [class]="
            'v-one-input__chevron' +
            (isDropdownOpen() ? ' v-one-input__chevron--rotate' : '')
          "
        >
          <v-one-icon
            [name]="'chevronDown'"
            [color]="
              hasError() ? 'var(--color-danger-600)' : 'var(--color-gray-700)'
            "
          />
        </div>
      </div>

      <!-- Custom Dropdown List -->
      @if (isDropdownOpen()) {
        <ul
          class="v-one-input__dropdown"
          [class.v-one-input__dropdown--up]="dropdownPosition() === 'up'"
          [class.v-one-input__dropdown--down]="dropdownPosition() === 'down'"
          [class.v-one-input__dropdown--ios]="isIOS()"
          [style.top]="selectDropdownStyles().top"
          [style.bottom]="selectDropdownStyles().bottom"
          [style.left]="selectDropdownStyles().left"
          [style.width]="selectDropdownStyles().width"
          (mousedown)="$event.preventDefault()"
          role="listbox"
        >
          @for (option of dropdownOptions(); track option.value) {
            <li
              class="v-one-input__option"
              [class.v-one-input__option--selected]="value() === option.value"
              [class.v-one-input__option--disabled]="option.disabled"
              [class.v-one-input__option--placeholder]="option.value === ''"
              [attr.role]="'option'"
              [attr.tabindex]="option.disabled ? -1 : 0"
              [attr.aria-selected]="value() === option.value"
              [attr.aria-disabled]="option.disabled"
              (click)="onOptionClick(option)"
              (keydown.enter)="onOptionClick(option)"
              (keydown.space)="onOptionClick(option)"
              (mousedown)="$event.preventDefault()"
            >
              {{ option.label }}
            </li>
          }
        </ul>
      }
    </div>
  }

  <!-- Autocomplete Input -->
  @if (type() === 'autocomplete') {
    <div class="v-one-input__wrapper">
      <!-- Left Icon -->
      @if (leftIcon()) {
        <div class="v-one-input__icon v-one-input__icon--left">
          <v-one-icon
            [name]="leftIcon()"
            [color]="
              hasError() ? 'var(--color-danger-600)' : 'var(--color-gray-700)'
            "
          />
        </div>
      }

      <!-- Autocomplete Input Element -->
      <input
        #inputRef
        [id]="'input-autocomplete-' + label()"
        type="text"
        [value]="autocompleteDisplayValue()"
        [attr.maxlength]="maxlength()"
        [placeholder]="placeholder()"
        [disabled]="isDisabled()"
        [readonly]="readonly()"
        [required]="required()"
        [attr.aria-label]="label()"
        [attr.aria-invalid]="hasError()"
        [attr.aria-required]="required()"
        [attr.aria-autocomplete]="'list'"
        [attr.aria-expanded]="isAutocompleteOpen()"
        class="v-one-input__field"
        (input)="onAutocompleteInput($event)"
        (focus)="onAutocompleteFocus()"
        (blur)="onAutocompleteBlur()"
        (keydown)="onKeydown($event)"
        autocomplete="off"
      />

      <!-- Label -->
      <label class="v-one-input__label" [for]="'input-autocomplete-' + label()">
        {{ label() }}
        @if (required()) {
          <span class="v-one-input__required">*</span>
        }
      </label>

      <!-- Clear Button -->
      @if (showClearButton() && autocompleteDisplayValue()) {
        <button
          type="button"
          class="v-one-input__clear"
          (mousedown)="$event.preventDefault()"
          (click)="onClear(); autocompleteDisplayValue.set('')"
          [attr.aria-label]="'Clear input'"
          tabindex="-1"
        >
          <v-one-icon
            [name]="'close'"
            [color]="'var(--color-black)'"
            [type]="ICON_TYPE.FILLED"
          />
        </button>
      }

      <!-- Autocomplete Dropdown -->
      @if (isAutocompleteOpen() && autocompleteOptions().length > 0) {
        <ul
          class="v-one-input__autocomplete-dropdown"
          [class.v-one-input__autocomplete-dropdown--ios]="isIOS()"
          [class.v-one-input__autocomplete-dropdown--up]="
            dropdownPosition() === 'up'
          "
          [class.v-one-input__autocomplete-dropdown--down]="
            dropdownPosition() === 'down'
          "
          [style.top]="autocompleteDropdownStyles().top"
          [style.left]="autocompleteDropdownStyles().left"
          [style.width]="autocompleteDropdownStyles().width"
          (mousedown)="$event.preventDefault()"
          role="listbox"
        >
          @for (option of autocompleteOptions(); track option.value) {
            <li
              class="v-one-input__autocomplete-option"
              [class.v-one-input__autocomplete-option--disabled]="
                option.disabled
              "
              [attr.role]="'option'"
              [attr.tabindex]="option.disabled ? -1 : 0"
              [attr.aria-disabled]="option.disabled"
              (click)="!option.disabled && onAutocompleteOptionClick(option)"
              (keydown.enter)="
                !option.disabled && onAutocompleteOptionClick(option)
              "
              (keydown.space)="
                !option.disabled && onAutocompleteOptionClick(option)
              "
              (mousedown)="$event.preventDefault()"
            >
              {{ option.label }}
            </li>
          }
        </ul>
      }
    </div>
  }

  <!-- Error Message -->
  @if (hasError()) {
    <div class="v-one-input__error" role="alert">
      <span class="v-one-input__error-text">{{ errorMessage() }}</span>
    </div>
  }
</div>
